import React from 'react'
import { SelectChangeEvent } from '@mui/material'
import { useForm, UseFormReturn } from 'react-hook-form'

import { PhoneInput, TextInput } from '../../../base'
import { inputProperties } from '../../../types/inputProperties'
import Address from '../address/Address'
import { validators } from '../../../utils/validators'
import CategorySelect from './components/CategorySelect'
import WidgetBody from '../../../base/Widget/WidgetBody'
import InputGroup from '../../../base/InputGroup/InputGroup'
import {
  getCategories,
  getSubCategoriesFromCategory,
  CATEGORY_HIERARCHY_MAP,
} from '../../../utils/categories'
import WidgetSection from '../../../base/Widget/WidgetSection'

export interface RecipientInformationProps {
  backLabel?: string
  category?: inputProperties
  categorySubtitle?: string
  city?: inputProperties
  continueLabel?: string
  country?: inputProperties
  email?: inputProperties
  form?: UseFormReturn
  line1?: inputProperties
  line2?: inputProperties
  onClickBack?: Function
  onSubmit?: Function
  phone?: inputProperties
  recipientName?: inputProperties
  state?: inputProperties
  subCategory?: inputProperties
  postalCode?: inputProperties
  title: string
  isStart?: boolean
}

const RecipientInformation = (props: RecipientInformationProps) => {
  const useFormDefault = props.form || useForm()

  const recipientNameProps = Object.assign(
    RecipientInformation.defaultProps.recipientName,
    props.recipientName,
  )
  const emailProps = Object.assign(
    RecipientInformation.defaultProps.email,
    props.email,
  )
  const phoneProps = Object.assign(
    RecipientInformation.defaultProps.phone,
    props.phone,
  )
  const categoryProps = Object.assign(
    RecipientInformation.defaultProps.category,
    props.category,
  )
  const subCategoryProps = Object.assign(
    RecipientInformation.defaultProps.subCategory,
    props.subCategory,
  )
  const countryProps = Object.assign(
    RecipientInformation.defaultProps.country,
    props.country,
  )
  const line1Props = Object.assign(
    RecipientInformation.defaultProps.line1,
    props.line1,
  )
  const line2Props = Object.assign(
    RecipientInformation.defaultProps.line2,
    props.line2,
  )
  const cityProps = Object.assign(
    RecipientInformation.defaultProps.city,
    props.city,
  )
  const stateProps = Object.assign(
    RecipientInformation.defaultProps.state,
    props.state,
  )
  const postalCodeProps = Object.assign(
    RecipientInformation.defaultProps.postalCode,
    props.postalCode,
  )

  const {
    formState: { isSubmitting },
    setValue,
  } = useFormDefault

  const onClickContinue = () => {
    props.onSubmit && props.onSubmit()
  }

  const onClickBack = () => {
    props.onClickBack && props.onClickBack()
  }

  const [subcategories, setSubcategories] = React.useState(
    getSubCategoriesFromCategory(categoryProps?.value),
  )

  const handleOnChangeCategory = (event: SelectChangeEvent<string>) => {
    const subCategories = getSubCategoriesFromCategory(event?.target?.value)
    setSubcategories(subCategories)
    if (subCategories.length >= 1) {
      setValue('subCategory', '')
    } else {
      const topLevelCategory = CATEGORY_HIERARCHY_MAP[event?.target?.value]
      setValue('subCategory', topLevelCategory.value)
    }
  }

  return (
    <WidgetBody
      onBack={props.isStart ? undefined : onClickBack}
      onContinue={onClickContinue}
      title={props.title}
      isSubmitting={isSubmitting}
    >
      <WidgetSection title='Recipient Information'>
        <InputGroup
          inputRows={[
            [
              <TextInput
                form={useFormDefault}
                disabled={recipientNameProps?.disabled}
                id={recipientNameProps?.id}
                label={recipientNameProps?.label}
                name={recipientNameProps?.name}
                required={recipientNameProps?.required}
                value={recipientNameProps?.value}
              />,
            ],
            [
              <CategorySelect
                form={useFormDefault}
                category={categoryProps}
                items={getCategories()}
                subtitle={props.categorySubtitle}
                onChange={handleOnChangeCategory}
              />,
            ],
            ...(subcategories && subcategories.length > 0
              ? [
                  [
                    <CategorySelect
                      form={useFormDefault}
                      category={subCategoryProps}
                      items={subcategories}
                    />,
                  ],
                ]
              : [[]]),
          ]}
        />
      </WidgetSection>

      <WidgetSection title='Physical Address'>
        <Address
          form={useFormDefault}
          country={countryProps}
          line1={line1Props}
          line2={line2Props}
          city={cityProps}
          state={stateProps}
          postalCode={postalCodeProps}
        />
      </WidgetSection>

      <WidgetSection title='Contact Information'>
        <InputGroup
          inputRows={[
            [
              <TextInput
                form={useFormDefault}
                disabled={emailProps?.disabled}
                id={emailProps?.id}
                label={emailProps?.label}
                name={emailProps?.name}
                required={emailProps?.required}
                rules={{ validate: validators.emailFormat }}
                value={emailProps?.value}
              />,
            ],
            [
              <PhoneInput
                form={useFormDefault}
                disabled={phoneProps?.disabled}
                id={phoneProps?.id}
                label={phoneProps?.label}
                name={phoneProps?.name}
                required={phoneProps?.required}
                rules={{ validate: validators.phoneFormat }}
                value={phoneProps?.value}
              />,
            ],
          ]}
        />
      </WidgetSection>
    </WidgetBody>
  )
}

RecipientInformation.defaultProps = {
  email: {
    label: 'Email Address (Optional)',
    name: 'email',
  },
  recipientName: {
    label: 'Recipient Name',
    name: 'recipientName',
    required: true,
  },
  phone: {
    label: 'Phone Number',
    name: 'phone',
    required: true,
  },
  country: {
    required: true,
  },
  category: {
    required: true,
    label: 'Recipient category',
    name: 'category',
  },
  subCategory: {
    required: true,
    label: 'Sub Category',
    name: 'subCategory',
  },
  line1: {
    required: true,
  },
  line2: {},
  city: {
    required: true,
  },
  state: {
    required: true,
  },
  postalCode: {
    required: true,
  },
  categorySubtitle: 'Could you be more specific',
  title: 'Add Recipient',
  isStart: false,
}

export default RecipientInformation
